import { JsonApiRequestError, Mutation, Query } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { ComboboxItem } from '@mantine/core';
import { sample, Store } from 'effector';
import { or } from 'patronum';
import {
	AddBonusMutationParams,
	Bonus,
	createAddBonusMutation,
	createGetAccountQuery,
	createGetAccountsQuery,
	createGetGamesQuery,
	EntityWithName,
} from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AddBonusFormValue = {
	accountId: number | null;
	gameId: number | null;
	currency: string | null;
	activationThreshold: string;
	depositRate: string;
};

export type AddBonusModel = ModalFormModel<null, AddBonusFormValue> & {
	addBonusMutation: Mutation<AddBonusMutationParams, Bonus, JsonApiRequestError>;
	getAccountsQuery: Query<void, EntityWithName[], JsonApiRequestError>;
	$pending: Store<boolean>;
	$currencies: Store<string[]>;
	$accounts: Store<ComboboxItem[]>;
	$games: Store<ComboboxItem[]>;
};

export const createAddBonusModel = (): AddBonusModel => {
	const addBonusMutation = createAddBonusMutation();

	const getAccountsQuery = createGetAccountsQuery();

	const getAccountQuery = createGetAccountQuery();

	const getGamesQuery = createGetGamesQuery();

	const formModel = createModalFormModel<null, AddBonusFormValue>({
		fields: {
			accountId: {
				init: null,
				rules: [notEmptyRule],
			},
			gameId: {
				init: null,
				rules: [notEmptyRule],
			},
			currency: {
				init: null,
				rules: [notEmptyRule],
			},
			activationThreshold: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			depositRate: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const $games = getGamesQuery.$data.map((games) =>
		games ? games.map((game) => ({ value: `${game.id}`, label: game.title })) : [],
	);

	const $accounts = getAccountsQuery.$data.map((accounts) =>
		accounts
			? accounts.map((account) => ({
					value: `${account.id}`,
					label: account.name,
				}))
			: [],
	);

	const $currencies = getAccountQuery.$data.map((data) =>
		data ? data.wallets.filter(({ isLocked }) => !isLocked).map((wallet) => wallet.currency) : [],
	);

	const $pending = or(
		getAccountsQuery.$pending,
		getAccountQuery.$pending,
		addBonusMutation.$pending,
		getGamesQuery.$pending,
	);

	sample({
		clock: formModel.switcher.enabled,
		target: [
			formModel.form.fields.currency.disable,
			formModel.form.fields.gameId.disable,
			formModel.form.fields.accountId.disable,
			getAccountsQuery.start,
			getGamesQuery.start,
		],
	});

	chain(getAccountsQuery.finished.success, formModel.form.fields.accountId.enable);

	sample({
		clock: formModel.form.fields.accountId.$value,
		target: [formModel.form.fields.currency.disable, formModel.form.fields.currency.reset],
	});

	sample({
		source: formModel.form.fields.accountId.$value,
		filter: Boolean,
		target: getAccountQuery.start,
	});

	chain(getAccountQuery.finished.success, formModel.form.fields.currency.enable);

	chain(getGamesQuery.finished.success, formModel.form.fields.gameId.enable);

	sample({
		clock: formModel.form.submitted,
		source: formModel.form.$values,
		fn: (fields) => ({
			currency: fields.currency!,
			accountId: fields.accountId!,
			gameId: fields.gameId!,
			depositRate: Number(fields.depositRate) / 100,
			activationThreshold: Number(fields.activationThreshold),
		}),
		target: addBonusMutation.start,
	});

	notify(addBonusMutation.finished.success, 'add-bonus.success');

	chain(addBonusMutation.finished.success, formModel.switcher.disable);

	sample({
		clock: formModel.modalClosed,
		target: [
			formModel.form.reset,
			addBonusMutation.reset,
			getAccountQuery.reset,
			getAccountsQuery.reset,
			getGamesQuery.reset,
		],
	});

	return {
		addBonusMutation,
		getAccountsQuery,
		$accounts,
		$pending,
		$currencies,
		$games,
		...formModel,
	};
};
